/**
 * formatDate.js — v2.5.124 (Phase 11)
 * ─────────────────────────────────────────────────────────────────
 * Frontend-side date formatting helpers. Centralized so the
 * various league pages (Seasons, Dashboard, Schedule, etc.) all
 * render the same human-friendly format.
 *
 * Why we don't just `new Date(str).toLocaleDateString()`:
 * Postgres DATE columns serialize as ISO strings like
 * "2026-09-01T04:00:00.000Z" — midnight UTC. In time zones west
 * of UTC, that timestamp is _the day before_ in local time, so a
 * naive `new Date(...)` rendered with locale formatting can show
 * the previous day. We extract the YYYY-MM-DD prefix and
 * construct a local Date at noon to avoid that shift entirely.
 */

/**
 * Format a single date value into a short human-readable string.
 * Accepts:
 *   • YYYY-MM-DD                       (Postgres DATE)
 *   • YYYY-MM-DDTHH:MM:SS.sssZ          (full ISO)
 *   • JS Date instance
 * Returns the locale-formatted "Sep 1, 2026"-style string. Returns
 * the empty string for nullish input, and the original value as a
 * String() if the format isn't recognized (so debugging is easy).
 */
export function formatDate(input) {
  if (input == null || input === '') return '';
  if (input instanceof Date) {
    if (Number.isNaN(input.getTime())) return '';
    return input.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  }
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(input));
  if (!match) return String(input);
  const [, y, m, d] = match;
  // Construct at noon local to dodge DST boundary shifts that can
  // otherwise tip a midnight value into the previous day.
  const dt = new Date(Number(y), Number(m) - 1, Number(d), 12, 0, 0);
  if (Number.isNaN(dt.getTime())) return String(input);
  return dt.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/**
 * Format a date range. Returns "Sep 1, 2026 → Apr 1, 2027" or
 * just the start when end is missing. Returns empty string if
 * start is missing.
 */
export function formatDateRange(start, end) {
  if (!start) return '';
  const s = formatDate(start);
  if (!end) return s;
  return `${s} → ${formatDate(end)}`;
}
